import { contentBlocksAdapter } from "@/services/contentful/assemblies/page/adapter/content-blocks.adapter";
import { getBlockId } from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { ContentSliderPageBlock, PageBlock } from "@/types/page-block";

const DEFAULT_AUTOPLAY_DURATION_SECONDS = 5;
const MIN_AUTOPLAY_DURATION_SECONDS = 2;
const MAX_AUTOPLAY_DURATION_SECONDS = 30;
const MILLISECONDS_PER_SECOND = 1000;
const WIDE_COLUMN_WIDTH = 12;

const columnWidthMap = {
  narrow: 8,
  medium: 10,
  wide: 12,
} as const satisfies Record<
  NonNullable<ContentSliderInput["containerWidth"]>,
  8 | 10 | 12
>;

export interface ContentSliderInput {
  sys?: { id: string };
  __typename: "ContentSliderD2C";
  internalName: string;
  containerWidth?: "narrow" | "medium" | "wide" | null;
  behavior?: "slider" | "carousel" | null;
  autoplay?: boolean | null;
  autoplayDuration?: number | null;
  navigationLayout?: "dots" | "bars" | null;
  showNavigationArrows?: boolean | null;
  slidesCollection?: {
    items: Array<{ __typename?: string } | null>;
  } | null;
  uiConfiguration?: UiConfigurationInput;
}

function normalizeAutoplayDelay(duration?: number | null): number {
  const roundedDuration = Math.round(
    duration ?? DEFAULT_AUTOPLAY_DURATION_SECONDS
  );
  const durationSeconds = Math.min(
    MAX_AUTOPLAY_DURATION_SECONDS,
    Math.max(MIN_AUTOPLAY_DURATION_SECONDS, roundedDuration)
  );

  return durationSeconds * MILLISECONDS_PER_SECOND;
}

type SliderTheme = {
  backgroundColor?: ContentSliderPageBlock["backgroundColor"];
  headlineColor?: ContentSliderPageBlock["headlineColor"];
  color?: ContentSliderPageBlock["color"];
};

/**
 * Slides inherit the slider's theme and render as embedded, full-width,
 * padding-free sections. Each supported slide type gets its own context here;
 * unknown types pass through untouched so new slide content types only need a
 * registry entry plus their context overrides.
 */
function applySliderContext(block: PageBlock, theme: SliderTheme): PageBlock {
  if (block.__typename === "SectionTextImage") {
    return {
      ...block,
      backgroundColor: theme.backgroundColor,
      headlineColor: theme.headlineColor,
      color: theme.color,
      columnWidth: WIDE_COLUMN_WIDTH,
      embedded: true,
      paddingTop: "none",
      paddingBottom: "none",
    };
  }

  return block;
}

export function ContentSliderAdapter(
  item: ContentSliderInput,
  index: number,
  locale?: string,
  basePath?: string
): ContentSliderPageBlock {
  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  const theme: SliderTheme = {
    backgroundColor: uiConfig?.backgroundColor,
    headlineColor: uiConfig?.headlineColor,
    color: uiConfig?.textColor,
  };

  const rawSlides = (item.slidesCollection?.items ?? []).filter(
    (slide): slide is NonNullable<typeof slide> & { __typename: string } =>
      Boolean(slide?.__typename)
  );

  const slides = contentBlocksAdapter(
    rawSlides as Parameters<typeof contentBlocksAdapter>[0],
    locale,
    basePath
  ).map((block) => applySliderContext(block, theme));

  return {
    sys: item.sys,
    id: getBlockId(item, index),
    __typename: item.__typename,
    internalName: item.internalName,
    slides,
    columnWidth: item.containerWidth
      ? columnWidthMap[item.containerWidth]
      : WIDE_COLUMN_WIDTH,
    behavior: item.behavior ?? "slider",
    autoplay: item.autoplay ?? false,
    autoplayDelay: normalizeAutoplayDelay(item.autoplayDuration),
    navigationLayout: item.navigationLayout ?? "dots",
    showNavigationArrows: item.showNavigationArrows ?? true,
    backgroundColor: uiConfig?.backgroundColor,
    headlineColor: uiConfig?.headlineColor,
    color: uiConfig?.textColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}
